import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { campaignManagementService } from "services/campaign";
import { DEFAULT_TOKEN } from "utilities/constants";
import { toast } from "react-toastify";
import { RootState } from "store";
import { useSelector } from "react-redux";
import { AwsUploader } from "components/Uploader/AwsUploader";
import { Delete as DeleteIcon } from '@styled-icons/fluentui-system-regular/Delete';
import { candidateService } from 'services/candidate';
import { getFolderPathAfterDomainName } from "utilities/commonUtils";
import MultilineUrlInput from "components/MultilineUrlInput/MultilineUrlInput";

interface AddLeadDialogProps {
  open: boolean;
  onClose: () => void;
  expertId: string;
  jobId: string;
  campaignId: string;
  onActionPerformed?: () => void;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 2,
  borderRadius: "8px",
  outline: "none",
  border: "none",
};

const AddLeadDialog: React.FC<AddLeadDialogProps> = ({
  open,
  onClose,
  jobId,
  campaignId,
  onActionPerformed,
}) => {
  const [selectedOption, setSelectedOption] = useState("Paste profile URLs");
  const [profileUrls, setProfileUrls] = useState("");
  const [loading, setLoading] = useState(false);
  const [invalidUrls, setInvalidUrls] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>("");

  const expertId = useSelector((state: RootState) => state.auth.user.expertId);

  const handleClose = () => {
    onClose();
    setProfileUrls("");
    setSelectedOption("Paste profile URLs");
    setUploadedFileUrl("");
    setInvalidUrls(false);
  };

  const closeIconStyle = {
    position: "absolute",
    top: "16px",
    right: "16px",
    cursor: "pointer",
    color: "#787878",
    fontSize: "16px",
  };

  const handleFileUpload = (fileUrl: string) => {
    setUploadedFileUrl(fileUrl);
  };

  const handleDeleteFile = () => {
    if (uploadedFileUrl) {
      candidateService.deleteS3File({ expertId, path: getFolderPathAfterDomainName(uploadedFileUrl) })
        .then(() => {
          setUploadedFileUrl("");
          toast.success("File removed successfully");
        })
        .catch(err => {
          console.error('Unable to remove file:', err);
          toast.error("Failed to remove file");
        });
    }
  };

  const isValidLinkedInUrl = (url: string) => {
    const linkedInRegex = /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/[a-zA-Z0-9-_%]+\/?$/;
    return linkedInRegex.test(url.trim());
  };

  const handleAddLead = async () => {
    if (
      selectedOption === "Paste profile URLs" &&
      profileUrls.trim().length === 0
    ) {
      toast.error("Please provide profile URLs.");
      return;
    }

    if (selectedOption === "Upload CSV file" && !uploadedFileUrl) {
      toast.error("Please upload a CSV file.");
      return;
    }

    let profilesArray: string[] = [];
    let leadType = "";
    let documentUrl = "";

    if (selectedOption === "Paste profile URLs") {
      profilesArray = profileUrls
        .split('\n')
        .map(url => url.trim())
        .filter(url => url.length > 0);

      if (profilesArray.some((url) => !isValidLinkedInUrl(url))) {
        toast.error("One or more URLs are not valid LinkedIn profile URLs.");
        setInvalidUrls(true);
        return;
      }
      leadType = "PROFILES";
    } else if (selectedOption === "Upload CSV file") {
      leadType = "CSV";
      documentUrl = uploadedFileUrl;
    }

    if (!expertId) {
      toast.error("Expert ID is missing.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        token: DEFAULT_TOKEN,
        expertId: expertId,
        jobId,
        campaignId,
        leadType,
        profiles: profilesArray,
        documentUrl,
      };

      const response = await campaignManagementService.AddLead(payload);

      if (response) {
        toast.success("Leads added successfully");
        if (onActionPerformed) onActionPerformed();
        handleClose();
      }
    } catch (error) {
      console.error("Error adding leads:", error);
      toast.error("Failed to add leads");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="add-lead-dialog-title"
      aria-describedby="add-lead-dialog-description"
    >
      <Box sx={modalStyle}>
        <Typography
          id="add-lead-dialog-title"
          variant="h6"
          component="h2"
          style={{
            marginBottom: "16px",
            color: "#202020",
            fontSize: "18px",
            fontWeight: "500",
          }}
        >
          Create a list of leads below
        </Typography>
        <hr />

        <RadioGroup
          row
          aria-label="add-leads-method"
          name="add-leads-method"
          value={selectedOption}
          onChange={(e) => {
            setSelectedOption(e.target.value);
            if (e.target.value === "Paste profile URLs") {
              setUploadedFileUrl("");
            }
          }}
          style={{ marginBottom: "16px" }}
        >
          <FormControlLabel
            value="Paste profile URLs"
            control={<Radio sx={{ color: "#325CD6" }} />}
            label="Paste Profile URLs"
          />
          <FormControlLabel
            value="Upload CSV file"
            control={<Radio sx={{ color: "#325CD6" }} />}
            label="Upload CSV File"
          />
        </RadioGroup>

        {selectedOption === "Paste profile URLs" && (
          <MultilineUrlInput
            fullWidth
            rows={4}
            label="Insert the LinkedIn profile URLs into this field"
            placeholder="https://www.linkedin.com/in/john-doe/ "
            value={profileUrls}
            onChange={(value) => setProfileUrls(value)}
            onValidationChange={setInvalidUrls}
            validator={isValidLinkedInUrl}
          />
        )}
        {selectedOption === "Upload CSV file" && (
          <Box
            sx={{
              border: "1px solid #325CD6",
              borderRadius: "8px",
              padding: "16px",
              backgroundColor: "transparent",
            }}
          >
            {!uploadedFileUrl ? (
              <AwsUploader
                onUpload={handleFileUpload}
                directory={`${jobId}/campaign-leads/${campaignId}/add-lead`}
                expertId={expertId}
                allowAllUpload={true}
              />
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px",
                    border: "1px solid #ccc",
                    borderRadius: "6px",
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Typography sx={{ color: "#555", wordBreak: "break-all", fontWeight: "500" }}>
                    {uploadedFileUrl.split('/').pop()}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleDeleteFile}
                    color="error"
                    sx={{
                      padding: "4px",
                      backgroundColor: "#eee",
                      '&:hover': {
                        backgroundColor: "#ddd",
                      },
                    }}
                  >
                    <DeleteIcon size={20} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            marginTop: "16px",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            style={{ color: "#325CD6", borderColor: "#325CD6" }}
            disabled={loading}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            style={{
              backgroundColor: (selectedOption === "Paste profile URLs" && invalidUrls) 
                ? "#A9A9A9" // Grey color for invalid URLs
                : "#325CD6", // Default blue color
              color: "#fff",
              opacity: (selectedOption === "Paste profile URLs" && invalidUrls) ? 0.7 : 1
            }}
            onClick={handleAddLead}
            disabled={loading || 
              (selectedOption === "Paste profile URLs" && (invalidUrls || profileUrls.trim().length === 0)) ||
              (selectedOption === "Upload CSV file" && !uploadedFileUrl)
            }
          >
            {loading ? "Submitting..." : "Submit"}
          </Button>
        </Box>

        <Typography sx={closeIconStyle} onClick={handleClose}>
          &#10006;
        </Typography>
      </Box>
    </Modal>
  );
};

export default AddLeadDialog;
